import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["template", "target", "fields", "destroyField"]

    connect() {
        console.log("Custom Nested Form controller connected")
        // Assign unique IDs to existing fields for tracking
        this.fieldsTargets.forEach((field, index) => {
            if (!field.id) {
                field.id = `nested-fields-${Date.now()}-${index}`
            }
        })

        // Initialize duration selection tracking
        this.updateDurationSelections()
    }

    // Track selected durations and update dropdowns
    updateDurationSelections() {
        // Get all visible duration selects
        const durationSelects = this.getVisibleDurationSelects()

        // Get all currently selected durations
        const selectedDurations = this.getSelectedDurations(durationSelects)

        // Update each select to disable options that are already selected elsewhere
        durationSelects.forEach(select => {
            this.updateSelectOptions(select, selectedDurations)
        })
    }

    // Get all visible duration select dropdowns
    getVisibleDurationSelects() {
        const selects = []
        this.fieldsTargets.forEach(field => {
            // Skip fields marked for destruction (hidden)
            if (field.style.display === 'none') return

            const select = field.querySelector('select[name*="[duration]"]')
            if (select) selects.push(select)
        })
        return selects
    }

    // Get currently selected durations
    getSelectedDurations(selects) {
        const selectedDurations = {}

        selects.forEach(select => {
            if (select.value) {
                // Store by value with the select element as reference
                // This allows us to know which select has which value
                selectedDurations[select.value] = select
            }
        })

        return selectedDurations
    }

    // Update a select element's options
    updateSelectOptions(select, selectedDurations) {
        Array.from(select.options).forEach(option => {
            const value = option.value
            if (!value) return // Skip empty option

            // If this value is selected somewhere else, disable it
            if (selectedDurations[value] && selectedDurations[value] !== select) {
                option.disabled = true
                option.title = "This duration is already selected"
            } else {
                option.disabled = false
                option.title = ""
            }
        })
    }

    add(event) {
        event.preventDefault()

        // Clone the template content
        const content = this.templateTarget.content.cloneNode(true)

        // Replace NEW_RECORD with a unique ID
        const timestamp = new Date().getTime()
        const regex = new RegExp('NEW_RECORD', 'g')

        // Update all attributes with NEW_RECORD
        const html = content.querySelector('div[data-new-record]').outerHTML
        const newHtml = html.replace(regex, timestamp)

        // Create a temporary element to hold the new HTML
        const fragment = document.createElement('div')
        fragment.innerHTML = newHtml

        // Get the newly created select element
        const newSelect = fragment.querySelector('select[name*="[duration]"]')

        // Explicitly set the first option (prompt) as selected
        if (newSelect && newSelect.options.length > 0) {
            newSelect.selectedIndex = 0

            // Force the select to recognize the change
            newSelect.value = ""
        }

        // Insert the new element into the DOM
        this.targetTarget.insertAdjacentElement('beforeend', fragment.firstElementChild)

        // Add change listener to the new select
        if (newSelect) {
            newSelect.addEventListener('change', () => this.updateDurationSelections())
        }

        // Update durations after adding a new field
        this.updateDurationSelections()
    }

    remove(event) {
        event.preventDefault()

        // Find the fields container for this button
        const button = event.currentTarget
        const fieldsContainer = button.closest('.nested-fields')

        // Check if this is an existing record or a new one
        const destroyField = fieldsContainer.querySelector('input[name*="_destroy"]')

        if (destroyField) {
            // Mark for destruction
            destroyField.value = "1"

            // Hide the fields container
            fieldsContainer.style.display = 'none'
        } else {
            // Just remove it from the DOM if it's a new record
            fieldsContainer.remove()
        }

        // Update durations after removing a field
        this.updateDurationSelections()
    }

    // Called when any duration dropdown changes
    durationChanged(event) {
        this.updateDurationSelections()
    }
}